/**
 * Spearcamp (枪营) - Spearman training camp
 * Features: Green layered roof, three spear racks in front, red flag, open pavilion style
 * Size: 3x3 tiles (visual ~2.8 x 2.6)
 * Design: Open pavilion with prominent spear racks facing camera
 */

import * as THREE from 'three';

export function createSpearcampMesh(
  group: THREE.Group,
  mainColor: THREE.Color,
  accentColor: THREE.Color
): void {
  // Materials
  const stoneMat = new THREE.MeshStandardMaterial({ color: 0x7a7a7a, roughness: 0.9 });
  const woodMat = new THREE.MeshStandardMaterial({ color: 0x6d4c3d, roughness: 0.8 });
  const darkWoodMat = new THREE.MeshStandardMaterial({ color: 0x3d2817, roughness: 0.85 });
  const lightWoodMat = new THREE.MeshStandardMaterial({ color: 0x8b6914, roughness: 0.75 });
  const roofMat = new THREE.MeshStandardMaterial({ color: 0x2d5a3d, roughness: 0.7 }); // Green roof
  const roofEdgeMat = new THREE.MeshStandardMaterial({ color: 0x1a3d28, roughness: 0.6 });
  const metalMat = new THREE.MeshStandardMaterial({ color: 0xc0c0c0, roughness: 0.3, metalness: 0.7 });
  const redMat = new THREE.MeshStandardMaterial({ color: 0xcc2222, roughness: 0.6 });
  const flagMat = new THREE.MeshStandardMaterial({
    color: 0xcc2222,
    roughness: 0.6,
    side: THREE.DoubleSide
  });
  const glowMat = new THREE.MeshStandardMaterial({
    color: 0xffcc80,
    emissive: 0xff9800,
    emissiveIntensity: 0.5
  });

  // === Stone Platform Base (2.8 x 2.6) ===
  const baseGeom = new THREE.BoxGeometry(2.8, 0.22, 2.6);
  const base = new THREE.Mesh(baseGeom, stoneMat);
  base.position.y = 0.11;
  base.castShadow = true;
  base.receiveShadow = true;
  group.add(base);

  // Stone edge
  const edgeGeom = new THREE.BoxGeometry(2.9, 0.06, 2.7);
  const edge = new THREE.Mesh(edgeGeom, new THREE.MeshStandardMaterial({ color: 0x5a5a5a, roughness: 0.9 }));
  edge.position.y = 0.25;
  group.add(edge);

  // Front steps
  for (let i = 0; i < 2; i++) {
    const stepWidth = 0.8 - i * 0.05;
    const stepGeom = new THREE.BoxGeometry(stepWidth, 0.1, 0.2);
    const step = new THREE.Mesh(stepGeom, stoneMat);
    step.position.set(0, 0.05 + i * 0.1, 1.2 + i * 0.18);
    step.castShadow = true;
    group.add(step);
  }

  // === Wooden Floor ===
  const floorGeom = new THREE.BoxGeometry(2.4, 0.08, 2.2);
  const floor = new THREE.Mesh(floorGeom, woodMat);
  floor.position.y = 0.3;
  floor.receiveShadow = true;
  group.add(floor);

  // === Back Wall (partial, open design) ===
  const backWallGeom = new THREE.BoxGeometry(2.2, 1.0, 0.12);
  const backWall = new THREE.Mesh(backWallGeom, darkWoodMat);
  backWall.position.set(0, 0.84, -1.05);
  backWall.castShadow = true;
  group.add(backWall);

  // === Support Pillars (red/brown wood) ===
  const pillarGeom = new THREE.CylinderGeometry(0.06, 0.08, 1.3, 8);
  const pillarMat = new THREE.MeshStandardMaterial({ color: 0x8b4513, roughness: 0.7 });
  const pillarPositions = [
    { x: -1.1, z: 0.85 },
    { x: 1.1, z: 0.85 },
    { x: -1.1, z: -0.95 },
    { x: 1.1, z: -0.95 },
  ];
  for (const pos of pillarPositions) {
    const pillar = new THREE.Mesh(pillarGeom, pillarMat);
    pillar.position.set(pos.x, 0.99, pos.z);
    pillar.castShadow = true;
    group.add(pillar);
  }

  // === Green Layered Roof ===
  // Main roof base
  const roofBaseGeom = new THREE.BoxGeometry(2.9, 0.12, 2.65);
  const roofBase = new THREE.Mesh(roofBaseGeom, roofMat);
  roofBase.position.y = 1.72;
  roofBase.castShadow = true;
  group.add(roofBase);

  // Roof layer 2 (smaller)
  const roofLayer2Geom = new THREE.BoxGeometry(2.4, 0.1, 2.2);
  const roofLayer2 = new THREE.Mesh(roofLayer2Geom, roofMat);
  roofLayer2.position.y = 1.88;
  roofLayer2.castShadow = true;
  group.add(roofLayer2);

  // Roof layer 3 (smallest)
  const roofLayer3Geom = new THREE.BoxGeometry(1.8, 0.08, 1.7);
  const roofLayer3 = new THREE.Mesh(roofLayer3Geom, roofMat);
  roofLayer3.position.y = 2.02;
  roofLayer3.castShadow = true;
  group.add(roofLayer3);

  // Roof top cone
  const roofTopGeom = new THREE.ConeGeometry(0.9, 0.4, 4);
  const roofTop = new THREE.Mesh(roofTopGeom, roofMat);
  roofTop.position.y = 2.28;
  roofTop.rotation.y = Math.PI / 4;
  roofTop.castShadow = true;
  group.add(roofTop);

  // Roof edge trim
  const roofEdgeGeom = new THREE.BoxGeometry(3.0, 0.05, 2.75);
  const roofEdge = new THREE.Mesh(roofEdgeGeom, roofEdgeMat);
  roofEdge.position.y = 1.67;
  group.add(roofEdge);

  // Corner decorations (upturned eaves)
  const cornerGeom = new THREE.BoxGeometry(0.15, 0.06, 0.25);
  const cornerPositions = [
    { x: 1.4, z: 1.3, ry: -0.4 }, { x: -1.4, z: 1.3, ry: 0.4 },
    { x: 1.4, z: -1.3, ry: 0.4 }, { x: -1.4, z: -1.3, ry: -0.4 }
  ];
  for (const pos of cornerPositions) {
    const corner = new THREE.Mesh(cornerGeom, roofEdgeMat);
    corner.position.set(pos.x, 1.65, pos.z);
    corner.rotation.y = pos.ry;
    corner.rotation.z = 0.25;
    group.add(corner);
  }

  // === THREE SPEAR RACKS IN FRONT (facing camera) ===
  const createSpearRack = (xPos: number) => {
    const rackZ = 1.0; // In front, visible to camera

    // Rack base (horizontal wood piece)
    const rackBaseGeom = new THREE.BoxGeometry(0.45, 0.06, 0.12);
    const rackBase = new THREE.Mesh(rackBaseGeom, lightWoodMat);
    rackBase.position.set(xPos, 0.35, rackZ);
    group.add(rackBase);

    // Rack vertical supports
    const supportGeom = new THREE.BoxGeometry(0.05, 0.7, 0.06);
    const supportLeft = new THREE.Mesh(supportGeom, lightWoodMat);
    supportLeft.position.set(xPos - 0.18, 0.68, rackZ);
    group.add(supportLeft);

    const supportRight = new THREE.Mesh(supportGeom, lightWoodMat);
    supportRight.position.set(xPos + 0.18, 0.68, rackZ);
    group.add(supportRight);

    // Rack top bar
    const topBarGeom = new THREE.BoxGeometry(0.45, 0.04, 0.06);
    const topBar = new THREE.Mesh(topBarGeom, lightWoodMat);
    topBar.position.set(xPos, 1.02, rackZ);
    group.add(topBar);

    // Spears on rack (3-4 per rack)
    const spearOffsets = [-0.12, 0, 0.12];
    for (const offsetX of spearOffsets) {
      // Spear shaft (long pole)
      const shaftGeom = new THREE.CylinderGeometry(0.02, 0.025, 1.8, 6);
      const shaft = new THREE.Mesh(shaftGeom, woodMat);
      shaft.position.set(xPos + offsetX, 1.25, rackZ + 0.08);
      shaft.rotation.x = 0.08; // Slight tilt
      group.add(shaft);

      // Spear tip (metal)
      const tipGeom = new THREE.ConeGeometry(0.04, 0.2, 6);
      const tip = new THREE.Mesh(tipGeom, metalMat);
      tip.position.set(xPos + offsetX, 2.18, rackZ + 0.22);
      tip.rotation.x = 0.08;
      group.add(tip);

      // Red tassel (红缨)
      const tasselGeom = new THREE.CylinderGeometry(0.035, 0.02, 0.12, 8);
      const tassel = new THREE.Mesh(tasselGeom, redMat);
      tassel.position.set(xPos + offsetX, 2.0, rackZ + 0.18);
      tassel.rotation.x = 0.08;
      group.add(tassel);

      // Tassel strands
      const strandGeom = new THREE.CylinderGeometry(0.008, 0.003, 0.08, 4);
      for (let i = 0; i < 4; i++) {
        const strand = new THREE.Mesh(strandGeom, redMat);
        const angle = (i / 4) * Math.PI * 2;
        strand.position.set(
          xPos + offsetX + Math.cos(angle) * 0.02,
          1.92,
          rackZ + 0.16 + Math.sin(angle) * 0.02
        );
        strand.rotation.x = 0.2;
        group.add(strand);
      }
    }
  };

  // Three spear racks across the front
  createSpearRack(-0.8);
  createSpearRack(0);
  createSpearRack(0.8);

  // === Interior Glow (warm lighting inside) ===
  const glowGeom = new THREE.PlaneGeometry(0.8, 0.7);
  const glow = new THREE.Mesh(glowGeom, glowMat);
  glow.position.set(0, 0.7, 0.5);
  group.add(glow);

  // === Training Dummy (inside) ===
  const dummyPostGeom = new THREE.CylinderGeometry(0.04, 0.05, 0.8, 6);
  const dummyPost = new THREE.Mesh(dummyPostGeom, woodMat);
  dummyPost.position.set(-0.4, 0.74, -0.4);
  group.add(dummyPost);

  const dummyBodyGeom = new THREE.CylinderGeometry(0.12, 0.14, 0.4, 8);
  const dummyBodyMat = new THREE.MeshStandardMaterial({ color: 0xd4a574, roughness: 0.9 });
  const dummyBody = new THREE.Mesh(dummyBodyGeom, dummyBodyMat);
  dummyBody.position.set(-0.4, 1.08, -0.4);
  group.add(dummyBody);

  // Dummy arms
  const dummyArmGeom = new THREE.BoxGeometry(0.5, 0.06, 0.06);
  const dummyArm = new THREE.Mesh(dummyArmGeom, dummyBodyMat);
  dummyArm.position.set(-0.4, 1.15, -0.4);
  group.add(dummyArm);

  // Dummy head
  const dummyHeadGeom = new THREE.SphereGeometry(0.1, 8, 6);
  const dummyHead = new THREE.Mesh(dummyHeadGeom, dummyBodyMat);
  dummyHead.position.set(-0.4, 1.38, -0.4);
  group.add(dummyHead);

  // === Red Flag (right side) ===
  const flagPoleX = 1.55;
  const flagPoleZ = 0.5;

  // Flag pole
  const poleGeom = new THREE.CylinderGeometry(0.03, 0.04, 2.6, 6);
  const pole = new THREE.Mesh(poleGeom, woodMat);
  pole.position.set(flagPoleX, 1.6, flagPoleZ);
  pole.castShadow = true;
  group.add(pole);

  // Pole top ornament
  const poleTopGeom = new THREE.SphereGeometry(0.05, 8, 6);
  const poleTop = new THREE.Mesh(poleTopGeom, metalMat);
  poleTop.position.set(flagPoleX, 2.95, flagPoleZ);
  group.add(poleTop);

  // Red flag (plane, tilted forward for visibility)
  const flagGeom = new THREE.PlaneGeometry(0.65, 0.5);
  const flag = new THREE.Mesh(flagGeom, flagMat);
  flag.position.set(flagPoleX, 2.55, flagPoleZ + 0.4);
  flag.rotation.x = -0.5; // Tilt forward to be visible from isometric view
  group.add(flag);

  // Flag border
  const borderMat = new THREE.MeshStandardMaterial({ color: 0xffcc00, roughness: 0.5 });
  const borderGeom = new THREE.PlaneGeometry(0.7, 0.55);
  const border = new THREE.Mesh(borderGeom, borderMat);
  border.position.set(flagPoleX, 2.55, flagPoleZ + 0.38);
  border.rotation.x = -0.5;
  group.add(border);

  // Inner flag (smaller, on top of border)
  const innerFlagGeom = new THREE.PlaneGeometry(0.6, 0.45);
  const innerFlag = new THREE.Mesh(innerFlagGeom, flagMat);
  innerFlag.position.set(flagPoleX, 2.55, flagPoleZ + 0.42);
  innerFlag.rotation.x = -0.5;
  group.add(innerFlag);

  // === Weapon stand inside (back) ===
  const standGeom = new THREE.BoxGeometry(0.5, 0.08, 0.15);
  const stand = new THREE.Mesh(standGeom, woodMat);
  stand.position.set(0.5, 0.38, -0.7);
  group.add(stand);

  // Additional spears on back stand
  for (let i = 0; i < 2; i++) {
    const backShaftGeom = new THREE.CylinderGeometry(0.018, 0.022, 1.5, 6);
    const backShaft = new THREE.Mesh(backShaftGeom, woodMat);
    backShaft.position.set(0.35 + i * 0.3, 1.1, -0.7);
    backShaft.rotation.z = 0.15 - i * 0.3;
    group.add(backShaft);
  }
}
